import React, { useState, useEffect } from "react";
import {
  collection, getDocs, doc, updateDoc, deleteField, addDoc,
} from "firebase/firestore";
import { db } from "../firebase";

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatDateTime = (value) => {
  if (!value) return "—";
  let date;
  if (typeof value.toDate === "function") date = value.toDate();
  else if (value instanceof Date) date = value;
  else return String(value);
  return date.toLocaleString("de-DE", {
    day: "2-digit", month: "2-digit", year: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
};

const FIVE_DAYS_MS = 5 * 24 * 60 * 60 * 1000;

const isOlderThan5Days = (deliveredAt) => {
  if (!deliveredAt) return false;
  const ts = typeof deliveredAt.toDate === "function"
    ? deliveredAt.toDate().getTime()
    : Number(deliveredAt);
  return Date.now() - ts > FIVE_DAYS_MS;
};

// ── DHL status badge ──────────────────────────────────────────────────────────

const DHL_STATUS = {
  "delivered":             { bg: "#c6f6d5", color: "#276749", label: "Zugestellt" },
  "out-for-delivery":      { bg: "#bee3f8", color: "#2b6cb0", label: "Wird zugestellt" },
  "available-for-pickup":  { bg: "#fbd38d", color: "#7b341e", label: "Abholbereit (Filiale)" },
  "transit":               { bg: "#fefcbf", color: "#744210", label: "In Zustellung" },
  "pre-transit":           { bg: "#e9d8fd", color: "#553c9a", label: "Versandbereit" },
  "failure":               { bg: "#fff5f5", color: "#c53030", label: "Fehlgeschlagen" },
  "no-data":               { bg: "#fff8e1", color: "#b7791f", label: "Keine Daten!" },
  "unknown":               { bg: "#e2e8f0", color: "#4a5568", label: "Unbekannt" },
};

const DhlStatusCell = ({ order }) => {
  if (!order.dhlStatus) {
    return (
      <div>
        <span style={{
          background: "#e2e8f0", color: "#a0aec0",
          padding: "0.2em 0.55em", borderRadius: "10px",
          fontSize: "0.75rem", fontWeight: "600", whiteSpace: "nowrap",
        }}>
          — Noch nicht abgefragt
        </span>
      </div>
    );
  }
  const cfg = DHL_STATUS[order.dhlStatus] || DHL_STATUS["unknown"];
  return (
    <div>
      <span style={{
        background: cfg.bg, color: cfg.color,
        padding: "0.2em 0.55em", borderRadius: "10px",
        fontSize: "0.75rem", fontWeight: "600", whiteSpace: "nowrap",
      }}>
        {cfg.label}
      </span>
      {order.dhlStatusDescription && (
        <div style={{
          fontSize: "0.72rem", color: "#718096", fontStyle: "italic",
          marginTop: "0.2em", maxWidth: "200px",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}
          title={order.dhlStatusDescription}
        >
          {order.dhlStatusDescription}
        </div>
      )}
      {order.dhlStatus === "delivered" && order.dhlRecipientId && (
        <div style={{ fontSize: "0.72rem", color: "#276749", marginTop: "0.2em" }}>
          Empfangen: {order.dhlRecipientId}
        </div>
      )}
      {["transit", "out-for-delivery", "pre-transit"].includes(order.dhlStatus) &&
        order.dhlEstimatedDelivery && (
        <div style={{ fontSize: "0.72rem", color: "#744210", marginTop: "0.2em" }}>
          Voraussichtlich: {order.dhlEstimatedDelivery}
        </div>
      )}
      {order.dhlStatusUpdatedAt && (
        <div style={{ fontSize: "0.68rem", color: "#a0aec0", marginTop: "0.15em" }}>
          Stand: {formatDateTime(order.dhlStatusUpdatedAt)}
        </div>
      )}
      {order.pickupEmailSentAt && (
        <div style={{ marginTop: "0.3em" }}>
          <span
            title={`Abholinfo gesendet: ${formatDateTime(order.pickupEmailSentAt)}`}
            style={{
              background: "#fbd38d", color: "#7b341e",
              border: "1px solid #f6ad55",
              padding: "0.15em 0.5em", borderRadius: "10px",
              fontSize: "0.7rem", fontWeight: "600", cursor: "default",
              whiteSpace: "nowrap",
            }}
          >
            ✉ Abholinfo
          </span>
          {order.pickupReminderSentAt && (
            <span
              title={`Erinnerung gesendet: ${formatDateTime(order.pickupReminderSentAt)}`}
              style={{
                background: "#fed7aa", color: "#7b341e",
                border: "1px solid #f6ad55",
                padding: "0.15em 0.5em", borderRadius: "10px",
                fontSize: "0.7rem", fontWeight: "600", cursor: "default",
                whiteSpace: "nowrap", marginLeft: "0.3em",
              }}
            >
              ✉ Erinnerung
            </span>
          )}
        </div>
      )}
    </div>
  );
};

// ── Email status badge ────────────────────────────────────────────────────────

const EmailBadge = ({ queueEntry, orderId }) => {
  if (!queueEntry) {
    return <span style={{ color: "#a0aec0", fontSize: "0.8rem" }}>—</span>;
  }
  if (queueEntry.status === "sent") {
    return (
      <span
        title={queueEntry.sentAt ? `Gesendet: ${formatDateTime(queueEntry.sentAt)}` : ""}
        style={{
          background: "#c6f6d5", color: "#276749",
          border: "1px solid #9ae6b4",
          padding: "0.2em 0.55em", borderRadius: "10px",
          fontSize: "0.75rem", fontWeight: "600", cursor: "default",
        }}
      >
        ✓ Gesendet
      </span>
    );
  }
  if (queueEntry.status === "error") {
    return (
      <span
        title={`Bewertungs-E-Mail für #${orderId || "?"} nicht versendet.\n${queueEntry.errorMsg ? `Grund: ${queueEntry.errorMsg}` : "Ein Grund wurde nicht aufgezeichnet."}`}
        style={{
          background: "#fff5f5", color: "#c53030",
          border: "1px solid #fc8181",
          padding: "0.2em 0.55em", borderRadius: "10px",
          fontSize: "0.75rem", fontWeight: "600", cursor: "default",
        }}
      >
        ✗ Fehler
      </span>
    );
  }
  // pending
  return (
    <span style={{
      background: "#fefcbf", color: "#744210",
      border: "1px solid #f6e05e",
      padding: "0.2em 0.55em", borderRadius: "10px",
      fontSize: "0.75rem", fontWeight: "600",
    }}>
      Ausstehend
    </span>
  );
};

// ── Shipping notification email badge ────────────────────────────────────────

const ShippingEmailBadge = ({ entry, orderId, order, retrying, onRetry }) => {
  if (!entry) {
    if (order?.scheduledShippingAt) {
      const scheduled = order.scheduledShippingAt?.toDate
        ? order.scheduledShippingAt.toDate().toLocaleString("de-DE", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit",
          })
        : "";
      return (
        <span
          title={scheduled ? `Versand geplant: ${scheduled}` : "Versand geplant"}
          style={{
            background: "#ebf4ff", color: "#3b82f6",
            border: "1px solid #93c5fd",
            padding: "0.2em 0.55em", borderRadius: "10px",
            fontSize: "0.75rem", fontWeight: "600", cursor: "default",
          }}
        >
          Geplant
        </span>
      );
    }
    return <span style={{ color: "#a0aec0", fontSize: "0.8rem" }}>—</span>;
  }
  if (entry.status === "sent") {
    return (
      <span
        title={entry.sentAt ? `Gesendet: ${formatDateTime(entry.sentAt)}` : ""}
        style={{
          background: "#c6f6d5", color: "#276749",
          border: "1px solid #9ae6b4",
          padding: "0.2em 0.55em", borderRadius: "10px",
          fontSize: "0.75rem", fontWeight: "600", cursor: "default",
        }}
      >
        ✓ Gesendet
      </span>
    );
  }
  if (entry.status === "error") {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "4px", flexWrap: "wrap" }}>
        <span
          title={`Versand-E-Mail für #${orderId || "?"} nicht versendet.\n${entry.errorMsg ? `Grund: ${entry.errorMsg}` : "Ein Grund wurde nicht aufgezeichnet."}`}
          style={{
            background: "#fff5f5", color: "#c53030",
            border: "1px solid #fc8181",
            padding: "0.2em 0.55em", borderRadius: "10px",
            fontSize: "0.75rem", fontWeight: "600", cursor: "help",
          }}
        >
          ✗ Fehler
        </span>
        <button
          onClick={() => onRetry(order)}
          disabled={!!retrying[orderId]}
          style={{
            padding: "0.2em 0.55em", borderRadius: "10px",
            border: "1px solid #90cdf4",
            background: "#ebf8ff", color: "#2b6cb0",
            fontSize: "0.75rem", fontWeight: "600",
            cursor: retrying[orderId] ? "default" : "pointer",
            opacity: retrying[orderId] ? 0.6 : 1,
          }}
        >
          {retrying[orderId] ? "…" : "↺ Erneut"}
        </button>
      </div>
    );
  }
  return (
    <span style={{
      background: "#fefcbf", color: "#744210",
      border: "1px solid #f6e05e",
      padding: "0.2em 0.55em", borderRadius: "10px",
      fontSize: "0.75rem", fontWeight: "600",
    }}>
      Ausstehend
    </span>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const STATUS_FILTERS = [
  { key: "all",                  label: "Alle" },
  { key: "pre-transit",          label: "Versandbereit" },
  { key: "transit",              label: "In Zustellung" },
  { key: "out-for-delivery",     label: "Wird zugestellt" },
  { key: "available-for-pickup", label: "Abholbereit" },
  { key: "delivered",            label: "Zugestellt" },
  { key: "failure",              label: "Fehlgeschlagen" },
  { key: "no-data",              label: "Keine Daten!" },
  { key: "none",                 label: "Nicht abgefragt" },
];

const DeliveryPage = () => {
  const [orders, setOrders]                   = useState([]);
  const [emailMap, setEmailMap]               = useState({});
  const [shippingEmailMap, setShippingEmailMap] = useState({});
  const [retryingShipping, setRetryingShipping] = useState({});
  const [loading, setLoading]                 = useState(true);
  const [showOld, setShowOld]                 = useState(false);
  const [deleteMode, setDeleteMode]           = useState(false);
  const [selected, setSelected]               = useState(new Set());
  const [deleting, setDeleting]               = useState(false);
  const [search, setSearch]                   = useState("");
  const [statusFilter, setStatusFilter]       = useState("all");

  const handleRetryShipping = async (order) => {
    setRetryingShipping((r) => ({ ...r, [order.id]: true }));
    try {
      await addDoc(collection(db, "shipping_email_queue"), {
        orderId:        order.id,
        trackingNumber: order.trackingNumber,
        status:         "pending",
        createdAt:      new Date(),
      });
      setShippingEmailMap((prev) => ({
        ...prev,
        [order.id]: { status: "pending" },
      }));
    } catch (err) {
      alert(`Fehler beim erneuten Senden: ${err.message}`);
    } finally {
      setRetryingShipping((r) => ({ ...r, [order.id]: false }));
    }
  };

  const enterDeleteMode = () => { setDeleteMode(true); setSelected(new Set()); };
  const exitDeleteMode  = () => { setDeleteMode(false); setSelected(new Set()); };

  useEffect(() => {
    Promise.all([
      getDocs(collection(db, "etsy_orders")),
      getDocs(collection(db, "delivery_email_queue")),
      getDocs(collection(db, "shipping_email_queue")),
    ]).then(([ordersSnap, queueSnap, shippingQueueSnap]) => {
      // Build email status map: orderId → latest queue entry
      const map = {};
      const rank = { sent: 2, error: 1, pending: 0 };
      queueSnap.docs.forEach((d) => {
        const data = d.data();
        if (!data.orderId) return;
        // If multiple queue docs for same order, prefer "sent" > "error" > "pending"
        const existing = map[data.orderId];
        if (!existing || (rank[data.status] ?? -1) > (rank[existing.status] ?? -1)) {
          map[data.orderId] = data;
        }
      });
      setEmailMap(map);

      // Build shipping notification email status map
      const shippingMap = {};
      shippingQueueSnap.docs.forEach((d) => {
        const data = d.data();
        if (!data.orderId) return;
        const existing = shippingMap[data.orderId];
        if (!existing || (rank[data.status] ?? -1) > (rank[existing.status] ?? -1)) {
          shippingMap[data.orderId] = data;
        }
      });
      setShippingEmailMap(shippingMap);

      const withTracking = ordersSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((o) => o.trackingNumber && !o.isTestOrder)
        .sort((a, b) => {
          const ta = a.labelCreatedAt?.toMillis?.() ?? 0;
          const tb = b.labelCreatedAt?.toMillis?.() ?? 0;
          return tb - ta;
        });

      setOrders(withTracking);
    })
      .catch((err) => console.error("DeliveryPage load error:", err))
      .finally(() => setLoading(false));
  }, []);

  const activeOrders = orders.filter((o) => !isOlderThan5Days(o.deliveredAt));
  const hiddenCount  = orders.length - activeOrders.length;
  const ageFiltered  = showOld ? orders : activeOrders;

  const q = search.trim().toLowerCase();
  const displayed = ageFiltered.filter((o) => {
    if (statusFilter === "none") {
      if (o.dhlStatus) return false;
    } else if (statusFilter !== "all") {
      if (o.dhlStatus !== statusFilter) return false;
    }
    if (q) {
      return (
        (o.id || "").toLowerCase().includes(q) ||
        (o.buyerName || "").toLowerCase().includes(q) ||
        (o.trackingNumber || "").toLowerCase().includes(q) ||
        (o.customerEmail || "").toLowerCase().includes(q)
      );
    }
    return true;
  });

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === displayed.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(displayed.map((o) => o.id)));
    }
  };

  const handleDeleteTracking = async () => {
    if (selected.size === 0) return;
    const names = orders
      .filter((o) => selected.has(o.id))
      .map((o) => `${o.id} (${o.buyerName || "??"})`)
      .join("\n");
    if (!window.confirm(
        `Tracking-Nummer(n) für ${selected.size} Bestellung(en) löschen?\n\n${names}`,
    )) return;

    setDeleting(true);
    const cleared = {
      trackingNumber:       deleteField(),
      labelCreatedAt:       deleteField(),
      deliveredAt:          deleteField(),
      dhlStatus:             deleteField(),
      dhlStatusDescription:  deleteField(),
      dhlStatusUpdatedAt:    deleteField(),
      dhlRecipientId:        deleteField(),
      dhlEstimatedDelivery:  deleteField(),
    };
    try {
      await Promise.all(
          [...selected].map((id) =>
            updateDoc(doc(db, "etsy_orders", id), cleared),
          ),
      );
      setOrders((prev) => prev.filter((o) => !selected.has(o.id)));
      exitDeleteMode();
    } catch (err) {
      alert("Fehler beim Löschen: " + err.message);
    } finally {
      setDeleting(false);
    }
  };

  // ── Table styles ────────────────────────────────────────────────────────────
  const thStyle = {
    padding: "0.6em 0.85em", textAlign: "left",
    fontWeight: "700", fontSize: "0.75rem", color: "#4a5568",
    textTransform: "uppercase", letterSpacing: "0.04em",
    borderBottom: "2px solid #e2e8f0", background: "#f7fafc",
    whiteSpace: "nowrap",
  };
  const tdStyle = {
    padding: "0.7em 0.85em", fontSize: "0.85rem", color: "#2d3748",
    borderBottom: "1px solid #f0f0f0", verticalAlign: "top",
  };

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center",
        gap: "1em", flexWrap: "wrap", marginBottom: "1.25em",
      }}>
        <h2 style={{ margin: 0, fontSize: "1.25rem", color: "#1a202c" }}>
          Zustellung
        </h2>
        {!loading && orders.length > 0 && (
          <span style={{ fontSize: "0.82rem", color: "#718096" }}>
            {activeOrders.length} aktiv
            {hiddenCount > 0 && ` · ${hiddenCount} ausgeblendet (>5 Tage)`}
          </span>
        )}
        {hiddenCount > 0 && (
          <button
            onClick={() => setShowOld((v) => !v)}
            style={{
              padding: "0.3em 0.9em", borderRadius: "6px",
              border: "1px solid #cbd5e0", background: "#fff",
              color: "#4a5568", fontSize: "0.8rem", cursor: "pointer",
            }}
          >
            {showOld ? "Ältere ausblenden" : "Ältere anzeigen"}
          </button>
        )}
        {!loading && orders.length > 0 && !deleteMode && (
          <button
            onClick={enterDeleteMode}
            style={{
              padding: "0.3em 1em", borderRadius: "6px",
              border: "1px solid #e2e8f0", background: "#fff",
              color: "#718096", fontSize: "0.8rem", cursor: "pointer",
              marginLeft: "auto",
            }}
          >
            Tracking-Nr. löschen…
          </button>
        )}
        {deleteMode && (
          <>
            <span style={{ fontSize: "0.8rem", color: "#c53030", marginLeft: "auto" }}>
              {selected.size === 0
                ? "Bestellungen auswählen"
                : `${selected.size} ausgewählt`}
            </span>
            <button
              onClick={handleDeleteTracking}
              disabled={deleting || selected.size === 0}
              style={{
                padding: "0.3em 1em", borderRadius: "6px",
                border: "1px solid #fc8181", background: "#fff5f5",
                color: "#c53030", fontSize: "0.8rem", fontWeight: "600",
                cursor: (deleting || selected.size === 0) ? "not-allowed" : "pointer",
                opacity: selected.size === 0 ? 0.5 : 1,
              }}
            >
              {deleting ? "Wird gelöscht…" : "Löschen bestätigen"}
            </button>
            <button
              onClick={exitDeleteMode}
              disabled={deleting}
              style={{
                padding: "0.3em 0.9em", borderRadius: "6px",
                border: "1px solid #cbd5e0", background: "#fff",
                color: "#4a5568", fontSize: "0.8rem", cursor: "pointer",
              }}
            >
              Abbrechen
            </button>
          </>
        )}
      </div>

      {/* Search + filter */}
      {!loading && orders.length > 0 && (
        <div style={{ marginBottom: "1em", display: "flex", flexDirection: "column", gap: "0.6em" }}>
          <input
            type="text"
            placeholder="Suche nach Bestellung #, Name, Tracking-Nr. oder E-Mail…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: "100%", boxSizing: "border-box",
              padding: "0.5em 0.9em", borderRadius: "8px",
              border: "1px solid #cbd5e0", fontSize: "0.88rem",
              outline: "none", background: "#fff",
            }}
          />
          <div style={{ display: "flex", gap: "0.4em", flexWrap: "wrap" }}>
            {STATUS_FILTERS.map((f) => {
              const active = statusFilter === f.key;
              const cfg = f.key !== "all" && f.key !== "none"
                ? DHL_STATUS[f.key] : null;
              return (
                <button
                  key={f.key}
                  onClick={() => setStatusFilter(f.key)}
                  style={{
                    padding: "0.3em 0.85em", borderRadius: "20px",
                    border: active
                      ? `2px solid ${cfg ? cfg.color : "#553c9a"}`
                      : "1px solid #cbd5e0",
                    background: active
                      ? (cfg ? cfg.bg : "#e9d8fd")
                      : "#fff",
                    color: active
                      ? (cfg ? cfg.color : "#553c9a")
                      : "#4a5568",
                    fontSize: "0.8rem", fontWeight: active ? "700" : "500",
                    cursor: "pointer", whiteSpace: "nowrap",
                  }}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <p style={{ color: "#718096", fontStyle: "italic" }}>Lade Sendungen…</p>
      ) : displayed.length === 0 ? (
        <div style={{
          background: "#fff", borderRadius: "10px",
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
          padding: "3em", textAlign: "center", color: "#718096",
        }}>
          <p style={{ margin: 0 }}>
            {orders.length === 0
              ? "Noch keine Sendungen mit Tracking-Nummer."
              : "Keine Sendungen gefunden."}
          </p>
        </div>
      ) : (
        <div style={{
          background: "#fff", borderRadius: "10px",
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
          overflow: "auto",
        }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {deleteMode && (
                  <th style={{ ...thStyle, width: "36px", textAlign: "center" }}>
                    <input
                      type="checkbox"
                      checked={displayed.length > 0 && selected.size === displayed.length}
                      onChange={toggleSelectAll}
                      title="Alle auswählen"
                    />
                  </th>
                )}
                <th style={thStyle}>Bestellung #</th>
                <th style={thStyle}>Käufer</th>
                <th style={thStyle}>Tracking-Nr.</th>
                <th style={thStyle}>Etikett erstellt</th>
                <th style={thStyle}>DHL Status</th>
                <th style={thStyle}>Zugestellt am</th>
                <th style={thStyle}>Versand-E-Mail</th>
                <th style={thStyle}>E-Mail</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map((order) => (
                <tr key={order.id}
                  style={{
                    opacity: isOlderThan5Days(order.deliveredAt) ? 0.55 : 1,
                    background: deleteMode && selected.has(order.id) ? "#fff5f5" : "transparent",
                  }}
                >
                  {deleteMode && (
                    <td style={{ ...tdStyle, textAlign: "center" }}>
                      <input
                        type="checkbox"
                        checked={selected.has(order.id)}
                        onChange={() => toggleSelect(order.id)}
                      />
                    </td>
                  )}
                  <td style={tdStyle}>
                    <span style={{
                      color: "#553c9a", fontWeight: "600", fontSize: "0.82rem",
                    }}>
                      {order.id}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    {order.buyerName || "—"}
                  </td>
                  <td style={tdStyle}>
                    <a
                      href={`https://www.dhl.de/de/privatkunden/dhl-sendungsverfolgung.html?piececode=${order.trackingNumber}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        fontFamily: "monospace", fontSize: "0.8rem",
                        color: "#2b6cb0", textDecoration: "none",
                      }}
                    >
                      {order.trackingNumber}
                    </a>
                  </td>
                  <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                    {formatDateTime(order.labelCreatedAt)}
                  </td>
                  <td style={tdStyle}>
                    <DhlStatusCell order={order} />
                  </td>
                  <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                    {order.deliveredAt ? formatDateTime(order.deliveredAt) : "—"}
                  </td>
                  <td style={tdStyle}>
                    <ShippingEmailBadge
                      entry={shippingEmailMap[order.id]}
                      orderId={order.id}
                      order={order}
                      retrying={retryingShipping}
                      onRetry={handleRetryShipping}
                    />
                  </td>
                  <td style={tdStyle}>
                    <EmailBadge queueEntry={emailMap[order.id]} orderId={order.id} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && orders.length > 0 && (
        <p style={{
          marginTop: "0.75em", color: "#a0aec0",
          fontSize: "0.78rem", textAlign: "right",
        }}>
          {displayed.length} von {ageFiltered.length} Sendung{ageFiltered.length !== 1 ? "en" : ""}
          {hiddenCount > 0 && !showOld && ` · ${hiddenCount} ältere ausgeblendet`}
        </p>
      )}
    </div>
  );
};

export default DeliveryPage;
